/**
 * Persistence-aware welcome message hook (TRAC-637).
 *
 * Stores the first contextual welcome the user sees in ``localStorage`` and
 * renders that frozen copy on every subsequent mount in the same browser
 * for the same user. This is the user-visible promise of TRAC-637 —
 * "welcome survives reload" — delivered without server-side coordination,
 * dedup machinery, or PII redactor wiring.
 *
 * Why frontend-only:
 * - The load-bearing user behavior is "hard refresh preserves the welcome,"
 *   which localStorage delivers natively.
 * - The S3-backed earlier design was correct for a world that includes
 *   TRAC-638 (reload-resume), TRAC-639 (PDP-nav acks with server-side
 *   dedup), and TRAC-641 (per-user long-term chat-history store). None of
 *   those are in flight today; building infrastructure ahead of consumers
 *   means paying race-condition / observability / security costs without
 *   the matching value.
 * - When the long-term store ships, the access pattern (per-user reads)
 *   may favor DynamoDB rather than the sibling-S3-prefix layout we tried
 *   here, so the design decision should be made in that ticket's context.
 *
 * Cross-tab consistency: the ``storage`` event fires in other tabs of the
 * same origin when this tab writes — so a welcome written in tab A is
 * picked up by tab B on its next render. No additional sync wiring needed.
 *
 * Frozen-at-write semantics: same as the S3 design. The first contextual
 * welcome (i.e. the first time the user lands on a PDP) wins. Navigating
 * to a different PDP within the same browser session does NOT update the
 * stored welcome — the user sees the welcome they first got. Clear
 * conversation removes the stored value, allowing the next welcome to be
 * captured fresh.
 *
 * Skip-no-tire semantics: a generic welcome (no ``tire_name``) is NOT
 * persisted. This prevents the "Clear → pageContext briefly null → persist
 * the generic → lock onto it" race that the S3 version had to fight. The
 * generic still renders optimistically until pageContext arrives.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ChatMessage, MessageKind } from '../types/chat.types';
import { logger } from '../utils/logger';

// Single key per browser/origin. Earlier versions of this hook keyed by
// partner_token, but that rotates whenever the test harness (or a long-
// session re-mint) generates a fresh token — and the welcome rotated with
// it, defeating the "frozen at first welcome" promise. Production partner
// tokens can also rotate within a single user's visit; the welcome should
// survive that. The user IDENTITY for welcome purposes is "this browser
// origin," same scope as localStorage itself.
const STORAGE_KEY = 'traci.welcome.v1';
// Bumped to 2: invalidates welcomes captured before the SEO-title fix in
// EmbedPage.tsx (some users had "Longer CrossClimate2 page title for SEO data"
// frozen in storage from before tire_name was sourced from tireDetailData.make
// + model). v1-stored welcomes fail the schema check on read and get
// re-captured fresh with the correct tire name.
// Bumped to 3 (Miles, 2026-07-27): stored welcomes are scoped to the
// conversation they were captured in (conversation_id). A frozen welcome
// from an old conversation must not resurrect into a new one — pre-
// engagement the welcome floats with the current page; post-engagement it
// is frozen for THAT conversation (and still survives reloads). v2 entries
// fail the schema check on read and are recaptured fresh.
const STORAGE_SCHEMA_VERSION = 3;

/** Structural subset the hook needs; decouples from ChatInterface's local Message type. */
export interface WelcomeMessageLike {
  id: string;
  content: string;
  parts?: ReadonlyArray<{ type?: string; content?: unknown }> | undefined;
  kind?: MessageKind;
  starter_prompts?: string[];
}

export interface UsePersistedWelcomeArgs {
  /** The optimistic welcome the caller wants rendered before storage hydrate. */
  optimisticWelcome: WelcomeMessageLike | null;
  /** Optional tire name — without this we don't persist (avoids storing generic welcomes). */
  tireName?: string | null;
  /** Optional starter prompts to persist alongside the welcome text. */
  starterPrompts?: string[] | null;
  /** Disable the side-effects entirely. */
  enabled?: boolean;
  /**
   * Monotonic counter bumped by the caller (typically on Clear conversation).
   * When it changes, the hook clears its stored welcome so the next contextual
   * welcome lands fresh.
   */
  clearSignal?: number;
  /**
   * True once the user has actually engaged with the conversation (sent at
   * least one message). Pre-engagement, the welcome is treated as ephemeral
   * — it floats with the current pageContext so PDP nav updates the visible
   * greeting like a normal contextual welcome. The moment this flips to
   * true, we snapshot whatever welcome is currently being shown and persist
   * it. Subsequent renders hydrate from that snapshot regardless of
   * pageContext, so the welcome that's part of the user's conversation
   * history doesn't silently rewrite if they navigate.
   *
   * The caller computes this from its own state (e.g. ``messages.some(m =>
   * m.role === 'user')``). When the user clears the conversation, this
   * naturally returns to false alongside ``clearSignal``.
   */
  hasUserEngaged?: boolean;
  /**
   * The current conversation's agent session id. A stored welcome is only
   * honored inside the conversation it was captured in; null disables both
   * hydration and persistence (no conversation to scope to yet).
   */
  conversationId?: string | null;
}

export interface UsePersistedWelcomeResult {
  /** The welcome to render — stored when available, otherwise optimistic. */
  welcome: ChatMessage | WelcomeMessageLike | null;
  /** True once the storage read has resolved (always synchronous, kept for API stability). */
  hydrated: boolean;
}

interface StoredWelcome {
  schema_version: number;
  content: string;
  tire_name?: string;
  starter_prompts?: string[];
  /** Original Message id from optimistic build, preserved so React keys stay stable. */
  source_id?: string;
  /** ISO timestamp of first persist. Surfaced as message timestamp on render. */
  persisted_at: string;
  /** Conversation (agent session id) this welcome was frozen in. */
  conversation_id?: string;
}

function readStored(): StoredWelcome | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredWelcome;
    if (parsed.schema_version !== STORAGE_SCHEMA_VERSION) return null;
    if (typeof parsed.content !== 'string' || !parsed.content) return null;
    return parsed;
  } catch (err) {
    logger.warn('[TRAC-637] readStored failed', err);
    return null;
  }
}

function writeStored(value: StoredWelcome): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (err) {
    // Quota exceeded / private mode — log and move on. The optimistic
    // welcome still renders this session; we just lose persistence.
    logger.warn('[TRAC-637] writeStored failed', err);
  }
}

function clearStored(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    logger.warn('[TRAC-637] clearStored failed', err);
  }
}

function extractText(msg: WelcomeMessageLike | null): string {
  if (!msg) return '';
  if (msg.content && typeof msg.content === 'string') return msg.content;
  if (Array.isArray(msg.parts)) {
    for (const part of msg.parts) {
      if (part && typeof part === 'object' && 'content' in part && typeof part.content === 'string') {
        return part.content;
      }
    }
  }
  return '';
}

function toChatMessage(stored: StoredWelcome, fallback: WelcomeMessageLike | null): ChatMessage {
  const parts: ChatMessage['parts'] = [
    { type: 'text', content: stored.content },
  ];
  if (stored.starter_prompts && stored.starter_prompts.length > 0) {
    parts.push({
      type: 'follow_up_actions',
      actions: stored.starter_prompts.map((p) => ({ label: p, value: p })),
    } as never);
  }
  return {
    id: stored.source_id ?? fallback?.id ?? 'welcome-message',
    role: 'assistant',
    content: stored.content,
    timestamp: new Date(stored.persisted_at),
    // ``isClientOnly`` retains its broader semantic: "this message has no
    // associated agent trace, so feedback buttons don't apply." The welcome
    // is admin-authored regardless of whether it lives in localStorage or
    // not. Don't render feedback affordances on it.
    isClientOnly: true,
    kind: 'welcome',
    starter_prompts: stored.starter_prompts,
    parts,
  };
}

export function usePersistedWelcome(args: UsePersistedWelcomeArgs): UsePersistedWelcomeResult {
  const {
    optimisticWelcome,
    tireName,
    starterPrompts,
    enabled = true,
    clearSignal,
    hasUserEngaged = false,
    conversationId = null,
  } = args;

  const [stored, setStored] = useState<StoredWelcome | null>(() => {
    if (!enabled || typeof window === 'undefined') return null;
    return readStored();
  });

  // Reset on clearSignal bump. Inline (not in useEffect) so the displayed
  // welcome updates on the same render as the Clear action; avoids the
  // brief stale-render flash the S3 design had to fight.
  //
  // ``hasClearedThisMount`` prevents the persist effect from immediately
  // re-writing the same welcome the user just asked us to clear. Without
  // this guard, Clear would visibly do nothing (storage removed → effect
  // re-fires → identical content re-persisted → user sees no change).
  // Persistence resumes on the next mount.
  const lastClearSignalRef = useRef<number | undefined>(clearSignal);
  const hasClearedThisMountRef = useRef(false);

  // Re-read storage when ``enabled`` flips on. The initial useState
  // initializer runs once at mount, and at that moment ``enabled`` is
  // typically false because ``pageContextReady`` hasn't flipped yet.
  // Without this effect, ``stored`` stays null forever and a returning
  // user never sees their persisted welcome — they get the optimistic
  // generic instead. Found in dev verification (TRAC-637).
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    if (stored !== null) return;
    if (hasClearedThisMountRef.current) return;
    const fromStorage = readStored();
    if (fromStorage) setStored(fromStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
  if (clearSignal !== lastClearSignalRef.current) {
    lastClearSignalRef.current = clearSignal;
    clearStored();
    if (stored !== null) setStored(null);
    hasClearedThisMountRef.current = true;
  }

  // Listen for cross-tab writes so a welcome persisted in tab A surfaces
  // in tab B on its next render. This is the "cross-tab sync" capability
  // the localStorage layout gives us for free.
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setStored(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [enabled]);

  const welcomeContent = useMemo(() => extractText(optimisticWelcome), [optimisticWelcome]);

  // Persist the first contextual welcome we see. Conditions:
  // - feature enabled
  // - nothing stored yet (frozen-at-first-write)
  // - the optimistic welcome has real content
  // - we have a tire_name — i.e. this is a PDP-contextual welcome, not the
  //   generic "Share your vehicle…" fallback. Generic welcomes don't get
  //   persisted because in the test harness (and real PDP nav) there's a
  //   brief window after Clear where pageContext is null and we'd
  //   otherwise lock onto the wrong thing.
  useEffect(() => {
    if (!enabled) return;
    if (stored !== null) return;
    if (hasClearedThisMountRef.current) return;
    if (!welcomeContent || !tireName) return;
    // Wait until starter prompts are loaded. ChatInterface fetches them
    // asynchronously from /api/settings/starter-prompts (or admin config)
    // AFTER pageContext arrives, so there's a window where the welcome
    // exists with tire_name set but starterPrompts is still an empty array.
    // Persisting during that window locks in a welcome with no buttons —
    // and the next render (after starters load) hydrates from storage and
    // misses them entirely. Defer the persist until starters are present;
    // if a PDP genuinely has none, we just don't persist (acceptable).
    if (!starterPrompts || starterPrompts.length === 0) return;
    // Don't persist until the user has actually engaged with the
    // conversation (sent at least one message). Before engagement, the
    // welcome is ephemeral — it should float with the current pageContext
    // so PDP nav updates the visible greeting naturally. Persisting too
    // early locks the welcome to whatever PDP the user happened to land
    // on first, even if they bounced through it and engaged on a different
    // one. The moment this gate passes, we snapshot the CURRENT optimistic
    // welcome (i.e. the one tied to the PDP they engaged on) and freeze it.
    if (!hasUserEngaged) return;
    // No conversation id yet (session still initializing) — wait; the effect
    // re-runs when it lands, and the snapshot must carry its conversation.
    if (!conversationId) return;
    // Last-chance hydrate: the re-read effect above and the cross-tab
    // storage effect can populate storage between this render and now. If
    // they did, hydrate from that instead of persisting our optimistic on
    // top of it. Without this, two effects racing in the same render
    // cycle (re-read sees stored=null in closure, persist also sees null,
    // both fire setStored; persist wins by declaration order).
    const existing = readStored();
    if (existing !== null) {
      setStored(existing);
      return;
    }
    const next: StoredWelcome = {
      schema_version: STORAGE_SCHEMA_VERSION,
      content: welcomeContent,
      tire_name: tireName,
      starter_prompts: starterPrompts ?? undefined,
      source_id: optimisticWelcome?.id,
      persisted_at: new Date().toISOString(),
      conversation_id: conversationId,
    };
    writeStored(next);
    setStored(next);
  }, [enabled, stored, welcomeContent, tireName, starterPrompts, optimisticWelcome?.id, hasUserEngaged, conversationId]);

  const welcome = useMemo(() => {
    // Miles's rule: with messages in the chat the welcome is frozen (render
    // the stored snapshot, incl. across reloads); with no messages it floats
    // with the current page. A snapshot is only ever the frozen welcome OF
    // THIS conversation — one captured in an old conversation is ignored.
    if (stored && conversationId && stored.conversation_id === conversationId) {
      return toChatMessage(stored, optimisticWelcome);
    }
    return optimisticWelcome;
  }, [stored, optimisticWelcome, conversationId]);

  return { welcome, hydrated: true };
}

/**
 * Test-only helper: clear the stored welcome. Exposed so the dev console /
 * Playwright can simulate "fresh user" without going through the chat's
 * clear-conversation flow.
 */
export function _clearPersistedWelcomeForTesting(): void {
  clearStored();
}
